
/* eslint-disable max-len */
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Arr } from '../../../CodesForFoundation';
import { useObserverReturningStore } from '../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { SessionStorageService } from '../../../platform/StorageService';
import { roundClamp, toDegrees } from '../../../platform/geom/Math';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { Colors } from '../../../ui';
import { useDrag } from '../../../ui/Hooks';
import { useResolve } from '../../Container';
import { AdapterInfo } from '../../canvas/AdapterInfo';
import { Camera } from '../../canvas/Camera';
import { Cursor } from '../../canvas/Cursor';
import { EditorSession, useEditorSessionObserver } from '../../canvas/EditorSession';
import { Selection } from '../../canvas/Selection';
import { Phys } from '../../canvas/entities/Phys';
import { WidgetManager } from '../../canvas/workspace/widgets/WidgetManager';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { flexLayoutInArea } from './AutoLayoutCommands';
import { AutoLayoutToolWidget } from './AutoLayoutToolWidget';
import { EnforcedFlexLayoutConfig } from './FlexLayout';

const normalizeAngle = (angle: number = 0) => {
    angle = roundClamp(toDegrees(angle), 5);
    if (angle > 180) angle -= 360;
    if (angle < -180) angle += 360;
    return angle;
};

const scaleCursor = (angle: number = 0) => `scale-${normalizeAngle(angle)}`;

const swap = (frame: Point[], o: Point, n: Point) => {
    // Swap o for n in frame and return new array:
    const copy = [...frame];
    const i = copy.indexOf(o);
    copy[i] = n;
    return copy;
};

const useAttachedWidget = (bounds: Rect) => {

    const widgets = useResolve(WidgetManager);
    return useEffect(() => {

        const unrealize = () => void (widgets.delete('propertyStrip'));

        unrealize();

        const container = widgets.createContainer({
            name: 'propertyStrip',
            scale: 1.5,
            position: ['attached', { to: bounds, placement: 'top', offset: [2.5, 0], }],
            direction: 'row',
        });

        container.addAll([
            widgets.createInline({
                from: bounds.topLeft(),
                to: bounds.bottomRight(),
                ui: AutoLayoutToolWidget,
            })
        ]);

        widgets.add(container);

        return unrealize;

    }, [bounds]);
};

const useAdapterBounds = () => {
    const session = useResolve(EditorSession);
    const selection = session.get(Selection);
    const initial = useMemo(() => Phys.from(selection).bounds(), []);
    return useState(initial);
};

const useCloseOnSelectionEmpty = () => {
    const session = useResolve(EditorSession);
    const selection = useEditorSessionObserver(Selection);
    useEffect(() => {
        if (selection.size === 0) {
            session.update(AdapterInfo, AdapterInfo.empty);
        }
    }, [selection]);
};

const useAdapterStyling = (bounds: Rect) => {

    const camera = useObserverReturningStore(useResolve(Camera));
    const viewBounds = Rect.fromPoints(camera.toView(bounds));

    return {
        position: 'absolute' as const,
        ...viewBounds.toCSS(),
    };
};

type DragFrom = { base: Point[], from: Point };

const EdgeHandle = React.memo<{ frame: Point[], target: number, onChange(next: Point[]): void }>(({ frame, target, onChange }) => {

    const camera = useObserverReturningStore(useResolve(Camera));

    const area = useMemo(() => {
        const f = camera.toView(frame);
        const a = Arr.at(f, target);
        const b = Arr.at(f, target + 1);
        return Rect.fromPoints([a, b]).inflate(10);
    }, [frame, target, camera.getState()]);

    const cursor = useMemo(() => {
        const a = Arr.at(frame, target);
        const b = Arr.at(frame, target + 1);
        const v = Point.vector(a, b).perp().projectToXOrY();
        return Cursor.css(scaleCursor(v.angle()));
    }, [frame, target]);

    const onInit = useCallback((e: MouseEvent): DragFrom => {
        return {
            base: frame,
            from: camera.toModel(Point.from(e, 'client')),
        };
    }, [frame, target]);

    const onDrag = useCallback(({ base, from }: DragFrom, e: MouseEvent) => {
        const to = camera.toModel(Point.from(e, 'client'));

        const a = Arr.at(base, target);
        const b = Arr.at(base, target + 1);
        const c = Arr.at(base, target + 2);
        const d = Arr.at(base, target + 3);

        const axis = Point.vector(a, b).normalize().perp();
        const del = to.subtract(from).project(axis);

        let next = base;
        // Regular
        {
            next = swap(next, a, a.add(del));
            next = swap(next, b, b.add(del));
        }
        if (e.altKey) {
            next = swap(next, c, c.subtract(del));
            next = swap(next, d, d.subtract(del));
        }

        // DebugOverlay.rect('frame', Rect.fromPoints(next), 'blue');
        onChange(next);
        return true;

    }, [onChange]);

    const [onMouseDown] = useDrag(onInit, onDrag);
    const style = { ...area.toCSS(), cursor };

    return <Flexer absolute pointerEvents="all" style={style} onMouseDown={onMouseDown}>

    </Flexer>;
});

const CornerHandle = React.memo<{ frame: Point[], target: number, onChange(next: Point[]): void }>(({ frame, target, onChange }) => {
    const camera = useObserverReturningStore(useResolve(Camera));

    const area = useMemo(() => {
        const f = camera.toView(frame);
        const x = Arr.at(f, target);
        return Rect.fromCenter(x, 20);
    }, [frame, target, camera.getState()]);

    const cursor = useMemo(() => {
        const x = Arr.at(frame, target);
        const o = Arr.at(frame, target + 2);
        const v = Point.vector(x, o).rotate(Math.PI / 4).projectToXOrY().rotate(-Math.PI / 4);
        return Cursor.css(scaleCursor(v.angle()));
    }, [frame, target]);

    const onInit = useCallback((e: MouseEvent): DragFrom => {
        return {
            base: frame,
            from: camera.toModel(Point.from(e, 'client')),
        };
    }, [frame, target]);

    const onDrag = useCallback(({ base, from }: DragFrom, e: MouseEvent) => {
        const to = camera.toModel(Point.from(e, 'client'));

        const x = Arr.at(base, target);
        const o = Arr.at(base, target + 2);

        const del = to.subtract(from);

        const nx = x.add(del);
        const no = e.altKey ? o.subtract(del) : o;

        const next = Rect.fromPoints([nx, no]).points();
        onChange(next);

        return true;
    }, [onChange, target]);

    const [onMouseDown] = useDrag(onInit, onDrag);
    const style = { ...area.toCSS(), cursor };

    return <Flexer absolute pointerEvents="all" style={style} onMouseDown={onMouseDown} />;
});

export const AutoLayoutAdapter: FC<{ info: AdapterInfo }> = ({ info }) => {

    const dispatch = useResolve(CommandDispatcher);
    const tempData = useResolve(SessionStorageService);

    const [bounds, setBounds] = useAdapterBounds();
    const styling = useAdapterStyling(bounds);

    useCloseOnSelectionEmpty();
    useAttachedWidget(bounds);

    const onChangeBounds = useCallback((next: Point[]) => {
        const config = (tempData.get('deqo/ui/autoLayoutTool') || ['row', 'start', 'start']) as EnforcedFlexLayoutConfig;
        const nb = Rect.fromPoints(next);
        setBounds(nb);
        dispatch(flexLayoutInArea, [nb.topLeft(), nb.bottomRight(), ...config, 'none']);
    }, []);

    return <>
        <Flexer absolute pointerEvents="none" style={styling} border={`solid 2px ${Colors.positive}`} borderRadius={'2px'}>

        </Flexer>
        <EdgeHandle frame={bounds.points()} target={0} onChange={onChangeBounds} />
        <EdgeHandle frame={bounds.points()} target={1} onChange={onChangeBounds} />
        <EdgeHandle frame={bounds.points()} target={2} onChange={onChangeBounds} />
        <EdgeHandle frame={bounds.points()} target={3} onChange={onChangeBounds} />
        <CornerHandle frame={bounds.points()} target={0} onChange={onChangeBounds} />
        <CornerHandle frame={bounds.points()} target={1} onChange={onChangeBounds} />
        <CornerHandle frame={bounds.points()} target={2} onChange={onChangeBounds} />
        <CornerHandle frame={bounds.points()} target={3} onChange={onChangeBounds} />
    </>;
};
